import { BottomSheet, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DocumentStatus } from "@src/appV2/Accounts/Documents/types";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { BookabilityDecision, type BookabilityStatusItem } from "@src/appV2/OpenShifts/ShiftAction";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { DocumentSubmissionApprovedBottomSheetContent } from "@src/appV2/redesign/Documents/Submission/ApprovedContent";
import { DocumentSubmissionInReviewBottomSheetContent } from "@src/appV2/redesign/Documents/Submission/InReviewContent";
import {
  SHIFT_DISCOVERY_SHIFT_HELLO_SIGN_CONTAINER_MODAL_PATH,
  SHIFT_DISCOVERY_SHIFT_MODAL_PATH,
} from "@src/appV2/redesign/ShiftDiscovery/paths";
import { type DocumentSubmittedModalRoutePathParams } from "@src/appV2/redesign/ShiftDiscovery/types";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import pluralize from "pluralize";
import { useParams } from "react-router-dom";

import { ShiftBottomSheetSkeleton } from "../../../BottomSheet/SheetSkeleton";

interface InstantReviewShiftDocumentSubmittedBottomSheetProps {
  modalState: UseModalState;
  bookabilityStatus?: BookabilityStatusItem;
}

export function InstantReviewShiftDocumentSubmittedBottomSheet(
  props: InstantReviewShiftDocumentSubmittedBottomSheetProps
) {
  const { modalState, bookabilityStatus } = props;

  const { shiftId, requirementId, documentStatus } =
    useParams<DocumentSubmittedModalRoutePathParams>();
  const { navigateToModal, blockingRequirementsContext, isLoadingShiftModalsData } =
    useShiftModalsDataContext();

  const isDocumentApproved = documentStatus === DocumentStatus.APPROVED;

  const {
    visibleBlockingRequirements,
    inReviewVisibleBlockingRequirements,
    notInReviewVisibleBlockingRequirements,
    instantReviewVisibleBlockingRequirements,
    areAllVisibleBlockedDocumentsInstantReview,
  } = blockingRequirementsContext ?? {};

  const inReviewBlockingDocumentsCount = inReviewVisibleBlockingRequirements?.length ?? 0;
  const nonInReviewBlockingDocumentsCount = notInReviewVisibleBlockingRequirements?.length ?? 0;
  const isAnyBlockingDocumentInstantReview =
    (instantReviewVisibleBlockingRequirements?.length ?? 0) > 0;

  function getContent() {
    if (nonInReviewBlockingDocumentsCount > 0) {
      return `Complete ${nonInReviewBlockingDocumentsCount} ${
        areAllVisibleBlockedDocumentsInstantReview ? "instant review" : "more"
      } ${pluralize("document", nonInReviewBlockingDocumentsCount)} before confirming your shift.`;
    }

    if (inReviewBlockingDocumentsCount > 0) {
      return `You have documents that are pending review before you can book this shift.`;
    }

    if (bookabilityStatus?.attributes.bookability.decision === BookabilityDecision.ALLOWED) {
      return "You can proceed to book your shift.";
    }

    return "Please complete the additional requirements to book this shift.";
  }

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter
          orientation="vertical"
          onClose={() => {
            modalState.closeModal();
          }}
        >
          {!isLoadingShiftModalsData && (
            <Button
              fullWidth
              size="large"
              onClick={() => {
                logEvent(
                  APP_V2_USER_EVENTS.SHIFT_BOOKING_FLOW_DOCUMENT_SUBMISSION_REVIEW_CTA_CLICKED,
                  {
                    shiftId,
                    submittedRequirementId: requirementId,
                    submittedDocumentStatus: documentStatus,
                    remainingBlockingDocumentsCount: visibleBlockingRequirements?.length,
                    isNextDocumentInstantReview: isAnyBlockingDocumentInstantReview,
                    remainingInstantReviewDocumentsCount:
                      instantReviewVisibleBlockingRequirements?.length,
                    nextInstantReviewDocumentRequirementId: isAnyBlockingDocumentInstantReview
                      ? instantReviewVisibleBlockingRequirements?.[0].requirementId
                      : undefined,
                  }
                );
                if (
                  isAnyBlockingDocumentInstantReview &&
                  isDefined(instantReviewVisibleBlockingRequirements?.[0])
                ) {
                  navigateToModal(SHIFT_DISCOVERY_SHIFT_HELLO_SIGN_CONTAINER_MODAL_PATH, {
                    pathParams: {
                      requirementId: instantReviewVisibleBlockingRequirements?.[0]?.requirementId,
                      shiftId,
                    },
                    forceReplacePath: true,
                  });
                } else {
                  navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
                    pathParams: { shiftId },
                    forceReplacePath: true,
                  });
                }
              }}
            >
              {isAnyBlockingDocumentInstantReview ? "Complete next document" : "Continue"}
            </Button>
          )}
        </DialogFooter>
      }
    >
      {isLoadingShiftModalsData ? (
        <ShiftBottomSheetSkeleton />
      ) : (
        <>
          {isDocumentApproved && (
            <DocumentSubmissionApprovedBottomSheetContent description={getContent()} />
          )}
          {!isDocumentApproved && (
            <DocumentSubmissionInReviewBottomSheetContent description={getContent()} />
          )}
        </>
      )}
    </BottomSheet>
  );
}
